import { useState, useEffect, useRef } from 'react';
import { createRoot } from 'react-dom/client';
import logoUrl from './assets/logo.png';
import portfolioHeroImg from './assets/portfolio-hero.png';
import portfolioSphereImg from './assets/portfolio-sphere.png';
import portfolioModalImg from './assets/portfolio-modal.png';

// ============ ICONS ============
const Icon = {
  arrow: (p) => (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" {...p}>
      <path d="M3 7h8m0 0L7.5 3.5M11 7l-3.5 3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  arrowUpRight: (p) => (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" {...p}>
      <path d="M4 10L10 4M10 4H5M10 4V9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  github: (p) => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" {...p}>
      <path d="M8 0C3.58 0 0 3.58 0 8a8 8 0 005.47 7.59c.4.07.55-.17.55-.38 0-.19-.01-.82-.01-1.49-2.01.37-2.53-.49-2.69-.94-.09-.23-.48-.94-.82-1.13-.28-.15-.68-.52-.01-.53.63-.01 1.08.58 1.23.82.72 1.21 1.87.87 2.33.66.07-.52.28-.87.51-1.07-1.78-.2-3.64-.89-3.64-3.95 0-.87.31-1.59.82-2.15-.08-.2-.36-1.02.08-2.12 0 0 .67-.21 2.2.82a7.42 7.42 0 014 0c1.53-1.04 2.2-.82 2.2-.82.44 1.1.16 1.92.08 2.12.51.56.82 1.27.82 2.15 0 3.07-1.87 3.75-3.65 3.95.29.25.54.73.54 1.48 0 1.07-.01 1.93-.01 2.2 0 .21.15.46.55.38A8.01 8.01 0 0016 8c0-4.42-3.58-8-8-8z" />
    </svg>
  ),
  linkedin: (p) => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" {...p}>
      <path d="M13.63 13.63h-2.37V9.92c0-.88-.02-2.02-1.23-2.02-1.23 0-1.42.96-1.42 1.96v3.77H6.24V6h2.27v1.04h.03c.32-.6 1.09-1.23 2.24-1.23 2.4 0 2.85 1.58 2.85 3.63v4.19zM3.56 4.96a1.37 1.37 0 110-2.75 1.37 1.37 0 010 2.75zM4.75 13.63H2.38V6h2.37v7.63zM14.81 0H1.18C.53 0 0 .52 0 1.16v13.68C0 15.48.53 16 1.18 16h13.63C15.47 16 16 15.48 16 14.84V1.16C16 .52 15.47 0 14.81 0z" />
    </svg>
  ),
  mail: (p) => (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" {...p}>
      <rect x="1.5" y="3" width="11" height="8" rx="1.5" stroke="currentColor" strokeWidth="1.3" />
      <path d="M2 4l5 4 5-4" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
    </svg>
  ),
  sun: (p) => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" {...p}>
      <circle cx="8" cy="8" r="3" stroke="currentColor" strokeWidth="1.4" />
      <path d="M8 1.5v1.5M8 13v1.5M14.5 8H13M3 8H1.5M12.6 3.4l-1.06 1.06M4.46 11.54L3.4 12.6M12.6 12.6l-1.06-1.06M4.46 4.46L3.4 3.4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  ),
  moon: (p) => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" {...p}>
      <path d="M13 9.5A5.5 5.5 0 016.5 3a5.5 5.5 0 105.5 6.5z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
    </svg>
  ),
};

// ============ NAV ============
function Nav({ theme, onToggleTheme, brandName = 'Fabrice', brandRole = 'Data Scientist · IA · ML engineer', showSocial = true }) {
  return (
    <nav className="nav">
      <div className="container nav-inner">
        <a href="#" className="brand">
          <span className="brand-logo"><img src={logoUrl} alt="logo" /></span>
          <span className="brand-name">
            <span>{brandName}</span>
            <span className="role" style={{ lineHeight: '1.05', fontSize: '14px' }}>{brandRole}</span>
          </span>
        </a>
        <div className="nav-links">
          <a className="nav-link" href="#projects">Projets</a>
          <a className="nav-link" href="#about">À propos</a>
          <a className="nav-link" href="#contact">Contact</a>
        </div>
        <div className="nav-cta">
          <button className="theme-toggle" onClick={onToggleTheme} aria-label="toggle theme">
            {theme === 'dark' ? <Icon.sun /> : <Icon.moon />}
          </button>
          {showSocial && <a className="btn btn-ghost" href="https://github.com/One-Up-Dev" target="_blank" rel="noreferrer"><Icon.github /> GitHub</a>}
          {showSocial && <a className="btn btn-ghost" href="https://www.linkedin.com/in/fabrice-sousa-a49118393/" target="_blank" rel="noreferrer"><Icon.linkedin /> LinkedIn</a>}
        </div>
      </div>
    </nav>
  );
}

// ============ HERO CHATBOT DATA ============
const CHAT_QA = [
  {
    id: 'stack',
    q: 'Quelle est ta stack technique ?',
    a: [
      { type: 'text', text: "Pour la donnée : Python (pandas, NumPy, scikit-learn), SQL, et plusieurs bases (MySQL, PostgreSQL, DuckDB)." },
      { type: 'chips', items: ['Python', 'pandas', 'NumPy', 'scikit-learn', 'SQL', 'MySQL', 'PostgreSQL', 'DuckDB', 'matplotlib', 'Seaborn', 'PowerBI', 'Git', 'GitHub', 'pip', 'uv'] },
      { type: 'text', text: "Pour aller plus vite : Claude Code dans mon workflow quotidien, Mistral et Gemini en prompting. Posture : piloter l'IA, pas la subir." },
    ],
  },
  {
    id: 'favoris',
    q: 'Tes types de projets favoris ?',
    a: [
      { type: 'text', text: "Je suis curieux par défaut — pas de chasse gardée sur les types de problèmes. Pour donner une idée, voici ce qui m'attire :" },
      { type: 'chips', items: ['Classification', 'NLP', 'Time series', 'Dataviz', 'Pipelines data', 'Automatisation IA', 'Recommendation', 'Anomaly detection'] },
      { type: 'text', text: "Côté secteurs : pas d'expertise data exclusive, prêt à apprendre. Mon avantage : un background vente et relation client — utile pour comprendre l'autre côté du tableau de bord." },
    ],
  },
  {
    id: 'apprentissages',
    q: 'Tes apprentissages récents ?',
    a: [
      { type: 'text', text: "Beaucoup de surface, encore peu en profondeur — mais ça avance. Côté tech ces derniers mois :" },
      { type: 'chips', items: ['Python (pandas/NumPy)', 'scikit-learn', 'MySQL', 'DuckDB', 'REST API', 'Git/GitHub', 'prompt engineering'] },
      { type: 'text', text: "Côté IA : Claude Code en workflow quotidien, cookbooks Anthropic et Mistral. La méthodologie (feature engineering, validation, déploiement) viendra avec la pratique terrain." },
    ],
  },
  {
    id: 'pourquoi',
    q: 'Pourquoi data science ?',
    a: [
      { type: 'text', text: "Deux choses me motivent : l'exploration de données et la résolution de problèmes concrets. L'IA générative est venue accélérer ça — elle change la manière dont on cadre, prototype et livre un projet data." },
      { type: 'text', text: "Mon cap : me spécialiser dans la conception de modèles IA adaptés aux besoins des entreprises — via des pipelines et de l'automatisation. Transformer un cas d'usage métier en système qui tourne, pas juste un notebook." },
    ],
  },
  {
    id: 'dispo',
    q: 'Es-tu disponible ?',
    a: [
      { type: 'text', text: "Oui — ouvert aux opportunités CDI / CDD / Freelance." },
      { type: 'text', text: "📍 France · Europe · Remote OK\n💼 Démarrage immédiat (fin de formation)\n💬 Réponse sous 24h en semaine" },
    ],
  },
  {
    id: 'contact',
    q: 'Comment te contacter ?',
    a: [
      { type: 'text', text: "Le plus simple : faire défiler jusqu'à la section contact en bas de page, ou cliquer sur « Me contacter » dans le hero." },
      { type: 'cta', label: 'Aller au contact', href: '#contact' },
    ],
  },
];

function HeroPreview() {
  const [messages, setMessages] = useState([
    { from: 'bot', kind: 'text', text: "👋 Bonjour ! Je suis l'assistant de ce portfolio. Posez-moi une question sur le profil." },
  ]);
  const [asked, setAsked] = useState(new Set());
  const [typing, setTyping] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, typing]);

  const askedRef = useRef(asked);
  askedRef.current = asked;
  const typingRef = useRef(typing);
  typingRef.current = typing;

  const ask = (qa) => {
    if (askedRef.current.has(qa.id) || typingRef.current) return;
    setAsked((s) => new Set(s).add(qa.id));
    setMessages((m) => [...m, { from: 'user', kind: 'text', text: qa.q }]);
    setTyping(true);
    let delay = 700;
    qa.a.forEach((block, i) => {
      setTimeout(() => {
        const { type, ...rest } = block;
        setMessages((m) => [...m, { from: 'bot', kind: type, ...rest }]);
        if (i === qa.a.length - 1) setTyping(false);
      }, delay);
      delay += 600;
    });
  };

  const reset = () => {
    setMessages([{ from: 'bot', kind: 'text', text: "👋 Bonjour ! Je suis l'assistant de ce portfolio. Posez-moi une question sur le profil." }]);
    setAsked(new Set());
  };

  const remaining = CHAT_QA.filter((qa) => !asked.has(qa.id));

  return (
    <div className="hero-preview hero-chat">
      <div className="hero-preview-head">
        <div className="hero-preview-dots"><span /><span /><span /></div>
        <span className="hero-chat-title">
          <span className="hero-chat-dot" />
          chat · discutez avec ce profil
        </span>
        <button className="hero-chat-reset" onClick={reset} aria-label="Recommencer la conversation" title="Recommencer">↻</button>
      </div>
      <div className="hero-chat-body" ref={scrollRef}>
        {messages.map((m, i) => <ChatMessage key={i} m={m} />)}
        {typing && (
          <div className="chat-row chat-row-bot">
            <div className="chat-bubble chat-bubble-bot chat-typing">
              <span /><span /><span />
            </div>
          </div>
        )}
      </div>
      <div className="hero-chat-suggest">
        {remaining.length === 0 ? (
          <button className="chat-chip chat-chip-reset" onClick={reset}>↻ Recommencer</button>
        ) : (
          remaining.slice(0, 3).map((qa) => (
            <button key={qa.id} className="chat-chip" onClick={() => ask(qa)} disabled={typing}>
              {qa.q}
            </button>
          ))
        )}
      </div>
    </div>
  );
}

function ChatMessage({ m }) {
  const side = m.from === 'user' ? 'user' : 'bot';
  if (m.kind === 'text') {
    return (
      <div className={`chat-row chat-row-${side}`}>
        <div className={`chat-bubble chat-bubble-${side}`}>
          {m.text.split('\n').map((line, i) => <div key={i}>{line}</div>)}
        </div>
      </div>
    );
  }
  if (m.kind === 'chips') {
    return (
      <div className={`chat-row chat-row-${side}`}>
        <div className={`chat-bubble chat-bubble-${side} chat-bubble-rich`}>
          <div className="chat-tagcloud">
            {m.items.map((t) => <span key={t} className="chat-tag">{t}</span>)}
          </div>
        </div>
      </div>
    );
  }
  if (m.kind === 'projects') {
    return (
      <div className={`chat-row chat-row-${side}`}>
        <div className={`chat-bubble chat-bubble-${side} chat-bubble-rich`}>
          <div className="chat-projects">
            {m.items.map((p, i) => (
              <div key={i} className="chat-project">
                <span className="chat-project-tag">{p.tag}</span>
                <div className="chat-project-title">{p.title}</div>
                <div className="chat-project-metric">{p.metric}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }
  if (m.kind === 'metrics') {
    return (
      <div className={`chat-row chat-row-${side}`}>
        <div className={`chat-bubble chat-bubble-${side} chat-bubble-rich`}>
          <div className="chat-metrics">
            {m.items.map((x, i) => (
              <div key={i} className="chat-metric">
                <div className="v">{x.v}</div>
                <div className="l">{x.l}</div>
                <div className="d">{x.d}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }
  if (m.kind === 'cta') {
    return (
      <div className={`chat-row chat-row-${side}`}>
        <div className={`chat-bubble chat-bubble-${side} chat-bubble-rich`}>
          <a className="btn btn-primary" href={m.href}>{m.label} →</a>
        </div>
      </div>
    );
  }
  return null;
}

function Hero() {
  return (
    <section className="hero">
      <div className="hero-grid-bg" />
      <div className="container">
        <div className="hero-split">
          <div className="hero-left">
            <span className="hero-tag">
              <span className="pill">v3</span>
              <span>Disponible · CDI / CDD / Freelance</span>
            </span>
            <h1 className="hero-title">
              Je transforme les données <br />
              en <span className="accent">décisions mesurables.</span>
            </h1>
            <p className="hero-sub">
              Data scientist explorateur, à l'aise pour piloter l'IA et faire parler la donnée. Chaque projet a un résultat mesurable et une décision business derrière. Mes projets racontent mieux ce que je sais faire qu'un CV statique. Explorez ce qui suit.
            </p>
            <div className="hero-cta">
              <a className="btn btn-primary btn-lg" href="#projects">Voir les projets <Icon.arrow /></a>
              <a className="btn btn-secondary btn-lg" href="#contact"><Icon.mail /> Me contacter</a>
            </div>
            <div className="hero-meta">
              <div className="hero-meta-item"><span className="v">5+</span><span className="l">années d'expérience</span></div>
              <div className="hero-meta-item"><span className="v">12</span><span className="l">modèles en production</span></div>
              <div className="hero-meta-item"><span className="v">4</span><span className="l">domaines métier</span></div>
              <div className="hero-meta-item"><span className="v">MSc</span><span className="l">data science</span></div>
            </div>
          </div>
          <HeroPreview />
        </div>
      </div>
    </section>
  );
}

// ============ TRUST BAR ============
const STACK_LOGOS = [
  {
    name: 'Python',
    svg: (
      <svg viewBox="0 0 40 40" width="32" height="32">
        <path d="M20 4c-4.4 0-7 1.8-7 4v3h7v1H10c-2.5 0-5 1.5-5 6s2.5 6 5 6h2v-3.5c0-2.5 2-4.5 4.5-4.5h7c2 0 3.5-1.5 3.5-3.5V8c0-2.5-2.5-4-7-4zm-2.5 3a1.5 1.5 0 110 3 1.5 1.5 0 010-3z" fill="#3776AB" />
        <path d="M20 36c4.4 0 7-1.8 7-4v-3h-7v-1h10c2.5 0 5-1.5 5-6s-2.5-6-5-6h-2v3.5c0 2.5-2 4.5-4.5 4.5h-7c-2 0-3.5 1.5-3.5 3.5V32c0 2.5 2.5 4 7 4zm2.5-3a1.5 1.5 0 110-3 1.5 1.5 0 010 3z" fill="#FFD43B" />
      </svg>
    ),
  },
  {
    name: 'scikit-learn',
    svg: (
      <svg viewBox="0 0 40 40" width="40" height="32">
        <circle cx="12" cy="20" r="6" fill="none" stroke="#F7931E" strokeWidth="2.5" />
        <circle cx="28" cy="20" r="6" fill="none" stroke="#29ABE2" strokeWidth="2.5" />
        <circle cx="20" cy="8" r="4" fill="#F7931E" opacity="0.7" />
        <path d="M16 20h8" stroke="#3D3D3D" strokeWidth="2" strokeLinecap="round" />
        <circle cx="12" cy="20" r="2" fill="#F7931E" />
        <circle cx="28" cy="20" r="2" fill="#29ABE2" />
      </svg>
    ),
  },
  {
    name: 'GitHub',
    svg: (
      <svg viewBox="0 0 40 40" width="32" height="32">
        <path fillRule="evenodd" clipRule="evenodd" fill="currentColor" d="M20 3C10.6 3 3 10.6 3 20c0 7.5 4.9 13.9 11.6 16.2.8.2 1.1-.4 1.1-.8v-2.8c-4.6 1-5.5-2.2-5.5-2.2-.8-1.9-1.8-2.4-1.8-2.4-1.5-1 .1-1 .1-1 1.6.1 2.5 1.7 2.5 1.7 1.5 2.5 3.8 1.8 4.7 1.4.2-1.1.6-1.8 1-2.2-3.6-.4-7.4-1.8-7.4-8.1 0-1.8.6-3.2 1.7-4.4-.2-.4-.7-2 .2-4.2 0 0 1.4-.4 4.5 1.7 1.3-.4 2.7-.5 4-.5s2.7.2 4 .5c3.1-2.1 4.5-1.7 4.5-1.7.9 2.2.3 3.8.2 4.2 1 1.1 1.7 2.6 1.7 4.4 0 6.3-3.8 7.6-7.4 8.1.6.5 1.1 1.5 1.1 3v4.4c0 .4.3 1 1.1.8C32.1 33.9 37 27.5 37 20 37 10.6 29.4 3 20 3z" />
      </svg>
    ),
  },
  {
    name: 'Docker',
    svg: (
      <svg viewBox="0 0 40 40" width="40" height="32">
        <rect x="4" y="19" width="32" height="12" rx="3" fill="#0db7ed" opacity="0.15" />
        {[0, 8, 16].map(x => (
          <rect key={x} x={6 + x} y="21" width="6" height="5" rx="1" fill="#0db7ed" />
        ))}
        {[0, 8].map(x => (
          <rect key={x} x={10 + x} y="15" width="6" height="5" rx="1" fill="#0db7ed" />
        ))}
        <rect x="18" y="9" width="6" height="5" rx="1" fill="#0db7ed" />
        <path d="M36 22c-1-3-4-3-5-2.5" stroke="#0db7ed" strokeWidth="1.5" strokeLinecap="round" />
        <circle cx="33" cy="20" r="1.5" fill="#0db7ed" />
      </svg>
    ),
  },
  {
    name: 'n8n',
    svg: (
      <svg viewBox="0 0 40 40" width="40" height="32">
        <circle cx="10" cy="20" r="5" fill="#EA4B71" />
        <circle cx="30" cy="20" r="5" fill="#EA4B71" />
        <circle cx="20" cy="20" r="5" fill="none" stroke="#EA4B71" strokeWidth="2" />
        <path d="M15 20h10" stroke="#EA4B71" strokeWidth="2" strokeDasharray="2 2" />
        <text x="20" y="38" textAnchor="middle" fontFamily="monospace" fontSize="7" fill="currentColor">n8n</text>
      </svg>
    ),
  },
  {
    name: 'Claude Code',
    svg: (
      <svg viewBox="0 0 40 40" width="32" height="32">
        <rect x="4" y="8" width="32" height="24" rx="4" fill="none" stroke="#CC785C" strokeWidth="2" />
        <path d="M13 16l-5 4 5 4" stroke="#CC785C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M27 16l5 4-5 4" stroke="#CC785C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M22 14l-4 12" stroke="#CC785C" strokeWidth="2" strokeLinecap="round" />
      </svg>
    ),
  },
  {
    name: 'Claude Design',
    svg: (
      <svg viewBox="0 0 40 40" width="32" height="32">
        <rect x="4" y="4" width="32" height="32" rx="8" fill="#CC785C" opacity="0.15" />
        <circle cx="20" cy="18" r="6" fill="none" stroke="#CC785C" strokeWidth="2" />
        <path d="M20 24v8M16 32h8" stroke="#CC785C" strokeWidth="2" strokeLinecap="round" />
        <circle cx="20" cy="10" r="2" fill="#CC785C" />
      </svg>
    ),
  },
];

function TrustBar() {
  const doubled = [...STACK_LOGOS, ...STACK_LOGOS];
  return (
    <section className="trust">
      <div className="container">
        <div className="trust-label">Stack maîtrisée · outils quotidiens</div>
      </div>
      <div className="trust-marquee">
        <div className="trust-track">
          {doubled.map((logo, i) => (
            <div key={i} className="trust-logo trust-logo-rich">
              {logo.svg}
              <span>{logo.name}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ============ PROJECTS ============
const PROJECTS = [
  {
    id: 1,
    featured: true,
    label: 'visualisation · churn dashboard',
    domain: 'Retail · 2025',
    type: 'Production',
    title: 'Modèle de prédiction de churn pour un retailer européen',
    desc: "Pipeline complet de feature engineering, entraînement (XGBoost + calibration) et déploiement temps réel via FastAPI/Kubernetes. Réduction du churn de 18% sur le segment cible.",
    stack: ['Python', 'XGBoost', 'FastAPI', 'Airflow', 'AWS'],
    impact: 'AUC 0.94 · €1.2M/an',
  },
  {
    id: 2,
    label: 'NLP · classifieur multilingue',
    domain: 'SaaS · 2024',
    type: 'Open source',
    title: 'Classifieur de tickets support multilingue',
    desc: "Fine-tuning d'un modèle transformer sur 12 langues pour router automatiquement les tickets vers les bonnes équipes. 92% de précision en production.",
    stack: ['PyTorch', 'HuggingFace', 'Docker'],
    impact: '−40% temps de traitement',
  },
  {
    id: 3,
    label: 'design IA · claude-design',
    domain: 'Personnel · 2026',
    type: 'IA',
    title: 'Portfolio data scientist prototypé avec Claude-Design',
    desc: "Test grandeur nature du nouvel outil de design d'Anthropic : de l'idée au site déployé en quelques heures. Design system custom, panel de tweaks live, modal d'étude de cas et chatbot statique inclus.",
    stack: ['Claude Design', 'Claude Code', 'HTML/CSS', 'JSX', 'Vercel'],
    impact: '~2h brief → prod',
    client: 'Projet personnel',
    period: '2026',
    role: 'Designer · Developer',
    team: 'Solo',
    cover: portfolioHeroImg,
    images: [
      { label: 'About · sphère 3D des compétences', src: portfolioSphereImg },
      { label: "Modal · structure narrative d'étude de cas", src: portfolioModalImg },
    ],
    context: [
      "Les LLM génératifs n'excellent pas en design : ils ont du mal à savoir ce qui constitue un bon résultat visuel, d'autant que cette appréciation dépend de l'utilisateur. Côté Claude Code, j'avais déjà exploré skills et output-styles design-orientés — utile mais limité.",
      "Très à l'aise avec les outils d'Anthropic, j'ai voulu tester Claude-Design — leur nouvel outil spécialisé — pour voir si une couche produit au-dessus du LLM permet de combler ce gap design.",
    ],
    approach: {
      intro: "Le produit étant nouveau, la méthodologie a été volontairement itérative et resserrée :",
      bullets: [
        "Fournir 2 références visuelles concrètes pour cadrer le style attendu",
        "Lister les composants à introduire : chatbot, carrousel, project cards, sphère de compétences",
        "Itérer à partir de la base produite par Claude-Design — peu de rounds suffisent",
        "Décision consciente de ne pas sur-optimiser : c'est un portfolio, pas un produit",
        "Push GitHub → déploiement Vercel",
      ],
    },
    results: [
      "Ratio résultat/temps investi excellent : 1h de design, ~30 min de vibe coding, 15 min de déploiement. 4 composants livrés en cohérence avec une identité visuelle solide.",
      "Au-delà du livrable, l'apprentissage métier : un produit de spécialisation posé par-dessus un LLM est extrêmement efficace. Claude-Design est un \"plugin\" relativement simple — mais qui apporte des résultats nettement supérieurs sur sa verticale.",
    ],
    quote: {
      text: "1h de design · 30 min de code · 15 min de déploiement. Une idée, quatre composants, un portfolio en ligne. Aussi exotique que productif.",
      author: "Note personnelle — bilan post-build",
    },
    learnings: [
      "Un LLM brut n'est pas suffisant pour le design — la spécialisation produit fait la différence",
      "Donner 2-3 références visuelles concrètes accélère drastiquement la convergence",
      "Savoir s'arrêter : un portfolio à 95% en ligne vaut mieux qu'à 100% sur le disque",
    ],
  },
  {
    id: 4,
    label: 'forecasting · time series',
    domain: 'Finance · 2023',
    type: 'R&D',
    title: 'Prévision de volatilité multi-actifs',
    desc: "Modèle hybride GARCH + LSTM pour forecaster la volatilité intraday sur un panier de 50 actifs. Backtests sur 5 ans de données tick.",
    stack: ['Python', 'PyTorch', 'kdb+'],
    impact: 'Sharpe +0.4',
  },
  {
    id: 5,
    label: 'recommandation · graph ML',
    domain: 'E-commerce · 2023',
    type: 'Production',
    title: 'Système de recommandation par graphes',
    desc: "Graph Neural Network pour recommandation personnalisée à partir des interactions utilisateurs. A/B test : +12% de CTR sur la home.",
    stack: ['DGL', 'Neo4j', 'Spark'],
    impact: '+12% CTR',
  },
  {
    id: 6,
    label: 'causal inference · pricing',
    domain: 'Marketplace · 2022',
    type: 'Étude',
    title: "Estimation causale de l'élasticité-prix",
    desc: "Application de double machine learning et de quasi-expériences pour estimer l'élasticité-prix par segment. A informé la stratégie de pricing dynamique.",
    stack: ['EconML', 'CausalML', 'R'],
    impact: '+€340k MRR',
  },
];

function ProjectModal({ p, onClose }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <div className="modal-head-meta">
            <span className="crumb">{p.domain} · {p.type}</span>
            <span className="title">{p.title}</span>
          </div>
          <button className="modal-close" onClick={onClose} aria-label="Fermer">
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M3 3l8 8M11 3l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
          </button>
        </div>

        <div className="modal-body">
          <div className="modal-hero">
            {p.cover ? (
              <img src={p.cover} alt={p.label} className="modal-hero-img" />
            ) : (
              <>
                <div className="modal-hero-stripes" />
                <span className="modal-hero-label">{p.label}</span>
              </>
            )}
          </div>

          <div className="modal-content">
            <div className="modal-meta-row">
              <div className="item"><span className="k">Client</span><span className="v">{p.client || 'Placeholder S.A.'}</span></div>
              <div className="item"><span className="k">Période</span><span className="v">{p.period || p.domain.split('·')[1]?.trim() || '2024'}</span></div>
              <div className="item"><span className="k">Rôle</span><span className="v">{p.role || 'Lead Data Scientist'}</span></div>
              <div className="item"><span className="k">Équipe</span><span className="v">{p.team || '4 personnes'}</span></div>
              <div className="item"><span className="k">Impact</span><span className="v">{p.impact}</span></div>
            </div>

            <div className="modal-section">
              <h3>Contexte</h3>
              <h2>{p.context ? 'Le problème business' : 'Le problème business · placeholder'}</h2>
              {p.context ? (
                p.context.map((para, i) => <p key={i}>{para}</p>)
              ) : (
                <>
                  <p>{p.desc}</p>
                  <p>Placeholder. Décrivez ici le contexte business : pourquoi ce projet a été lancé, quelles
                    contraintes (réglementaires, techniques, budgétaires) ont façonné l'approche, et quels
                    étaient les enjeux pour les équipes métier.</p>
                </>
              )}
            </div>

            <div className="modal-image">
              {p.images?.[0]?.src ? (
                <img src={p.images[0].src} alt={p.images[0].label} className="modal-image-img" />
              ) : (
                <>
                  <div className="modal-hero-stripes" />
                  <span className="ph">{p.images?.[0]?.label || 'Image · architecture du système · placeholder'}</span>
                </>
              )}
            </div>

            <div className="modal-section">
              <h3>Approche</h3>
              <h2>Méthodologie et choix techniques</h2>
              {p.approach ? (
                <>
                  {p.approach.intro && <p>{p.approach.intro}</p>}
                  <ul>{p.approach.bullets.map((b, i) => <li key={i}>{b}</li>)}</ul>
                </>
              ) : (
                <>
                  <p>Placeholder. Détaillez ici votre démarche : exploration des données, hypothèses
                    testées, modèles comparés (baselines puis itérations), validations croisées, métriques
                    choisies et pourquoi.</p>
                  <ul>
                    <li>EDA approfondie sur 3 ans d'historique · placeholder</li>
                    <li>Feature engineering · 47 variables dérivées</li>
                    <li>Comparaison baselines (régression logistique) vs gradient boosting</li>
                    <li>Calibration de probabilités via isotonic regression</li>
                    <li>Tests A/B en production sur 4 semaines</li>
                  </ul>
                </>
              )}
            </div>

            {p.context ? (
              p.images?.[1] && (
                p.images[2] ? (
                  <div className="modal-images-grid">
                    <div className="modal-image">
                      {p.images[1].src ? (
                        <img src={p.images[1].src} alt={p.images[1].label} className="modal-image-img" />
                      ) : (
                        <><div className="modal-hero-stripes" /><span className="ph">{p.images[1].label}</span></>
                      )}
                    </div>
                    <div className="modal-image">
                      {p.images[2].src ? (
                        <img src={p.images[2].src} alt={p.images[2].label} className="modal-image-img" />
                      ) : (
                        <><div className="modal-hero-stripes" /><span className="ph">{p.images[2].label}</span></>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="modal-image">
                    {p.images[1].src ? (
                      <img src={p.images[1].src} alt={p.images[1].label} className="modal-image-img" />
                    ) : (
                      <><div className="modal-hero-stripes" /><span className="ph">{p.images[1].label}</span></>
                    )}
                  </div>
                )
              )
            ) : (
              <div className="modal-images-grid">
                <div className="modal-image"><div className="modal-hero-stripes" /><span className="ph">{p.images?.[1]?.label || 'EDA · distributions'}</span></div>
                <div className="modal-image"><div className="modal-hero-stripes" /><span className="ph">{p.images?.[2]?.label || 'Feature importance'}</span></div>
              </div>
            )}

            {!p.context && (
              <div className="modal-section">
                <h3>Démo</h3>
                <h2>Le modèle en action</h2>
                <div className="modal-video">
                  <span className="video-duration">2:34</span>
                  <span className="video-label">Démo · placeholder</span>
                  <span className="play-btn">
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                      <path d="M5 3l12 7-12 7V3z"/>
                    </svg>
                  </span>
                </div>
              </div>
            )}

            <div className="modal-section">
              <h3>Stack technique</h3>
              <div className="modal-stack">
                {p.stack.map(t => <span key={t} className="tag">{t}</span>)}
                {!p.context && (
                  <>
                    <span className="tag">Git</span><span className="tag">CI/CD</span>
                    <span className="tag">Monitoring · Grafana</span>
                  </>
                )}
              </div>
            </div>

            <div className="modal-section">
              <h3>Résultats</h3>
              <h2>Impact mesurable</h2>
              {p.results ? (
                p.results.map((para, i) => <p key={i}>{para}</p>)
              ) : (
                <p>Placeholder. Listez ici les chiffres précis : métriques de modèle (AUC, precision,
                  recall) ET métriques business (revenu, coût économisé, NPS). Mettez en avant la
                  comparaison avant/après.</p>
              )}
              {p.quote ? (
                <div className="modal-quote">
                  "{p.quote.text}"
                  <span className="author">{p.quote.author}</span>
                </div>
              ) : (
                <div className="modal-quote">
                  "Le modèle a dépassé nos attentes : un ROI de 4x dès le premier trimestre,
                  avec une équipe qui a pleinement adopté l'outil." · placeholder
                  <span className="author">Sponsor · Direction Data — Placeholder S.A.</span>
                </div>
              )}
            </div>

            <div className="modal-section">
              <h3>Apprentissages</h3>
              <h2>Ce que je retiens</h2>
              <ul>
                {(p.learnings || [
                  "L'importance d'une discussion hebdomadaire avec les équipes métier · placeholder",
                  "Le calibration step est souvent négligé mais critique en production",
                  "Investir tôt dans l'observabilité paie au bout de 3 mois",
                ]).map((l, i) => <li key={i}>{l}</li>)}
              </ul>
            </div>
          </div>
        </div>

        <div className="modal-foot">
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--fg-soft)' }}>
            {p.context ? 'Étude de cas' : 'Étude de cas · placeholder'}
          </span>
          <div style={{ display: 'flex', gap: 8 }}>
            <a className="btn btn-secondary" href="#"><Icon.github /> Repo GitHub</a>
            <a className="btn btn-primary" href="#contact" onClick={onClose}>En discuter <Icon.arrow /></a>
          </div>
        </div>
      </div>
    </div>
  );
}

function ProjectCard({ p, onOpen }) {
  return (
    <article className={`card ${p.featured ? 'card-featured' : ''}`} onClick={() => onOpen(p)}>
      <div className="card-thumb">
        {p.cover ? (
          <img src={p.cover} alt={p.label} className="card-thumb-img" />
        ) : (
          <>
            <div className="card-thumb-stripes" />
            <span className="card-thumb-label">{p.label}</span>
          </>
        )}
      </div>
      <div className="card-body">
        <div className="card-meta">
          <span>{p.domain}</span>
          <span className="dot" />
          <span>{p.type}</span>
        </div>
        <h3 className="card-title">{p.title}</h3>
        <p className="card-desc">{p.desc}</p>
        <div className="card-stack">
          {p.stack.map(t => <span key={t} className="tag">{t}</span>)}
          <span className="tag tag-accent">{p.impact}</span>
        </div>
      </div>
      <div className="card-foot">
        <span>{p.context ? 'Étude de cas' : 'Étude de cas · placeholder'}</span>
        <span className="arrow">Lire <Icon.arrowUpRight /></span>
      </div>
    </article>
  );
}

function Projects() {
  const [filter, setFilter] = useState('Tous');
  const [openProject, setOpenProject] = useState(null);
  const filters = ['Tous', 'Production', 'IA', 'R&D', 'Open source', 'Mission', 'Étude'];
  const filtered = filter === 'Tous' ? PROJECTS : PROJECTS.filter(p => p.type === filter);
  return (
    <section className="section" id="projects">
      <div className="container">
        <div className="section-head">
          <span className="eyebrow">Projets · {PROJECTS.length} études de cas</span>
          <h2 className="section-title">Le travail d'abord, le CV ensuite.</h2>
          <p className="section-sub">
            Chaque projet est documenté avec le contexte business, l'approche, les résultats chiffrés
            et le code (quand il est public). Placeholder — remplacez par vos propres projets.
          </p>
        </div>
        <div className="filters">
          {filters.map(f => (
            <button key={f} className={`filter ${filter === f ? 'active' : ''}`} onClick={() => setFilter(f)}>{f}</button>
          ))}
        </div>
        <div className="cards-grid">
          {filtered.map(p => <ProjectCard key={p.id} p={p} onOpen={setOpenProject} />)}
        </div>
      </div>
      {openProject && <ProjectModal p={openProject} onClose={() => setOpenProject(null)} />}
    </section>
  );
}

// ============ ABOUT ============
function About({ showSphere = true, sphereSpeed = 0.25 }) {
  return (
    <section className="section" id="about" style={{ background: 'var(--bg-sunk)' }}>
      <div className="container">
        <div className="section-head reveal">
          <span className="eyebrow">À propos · profil & parcours</span>
        </div>
        <div className="about" style={!showSphere ? { gridTemplateColumns: '1fr', maxWidth: 760, margin: '0 auto' } : undefined}>
          <div className="about-text reveal" style={{ '--reveal-delay': '80ms' }}>
            <h3>Data scientist · pilote IA · curieux par défaut.</h3>
            <p>
              Issu d'un cursus en informatique de maintenance, j'ai longtemps travaillé au contact direct des clients — un terrain qui forge un sens du besoin métier et de la communication. L'essor de l'IA générative et l'automatisation (notamment via n8n) m'a ramené vers la technique : exploration de données, analyse, résolution de problèmes concrets — la data science s'est imposée naturellement. Je termine actuellement une formation Data Analyst certifiée RNCP à la Wild Code School, orientée volontairement vers la data science et l'IA appliquée.
            </p>
            <p>
              Côté méthode : curieux par défaut, je ne me ferme à aucun type de problème — classification, NLP, time series, automatisation IA. Je préfère piloter les outils IA plutôt que les subir : les cadrer, les orchestrer, reprendre la main quand ils dérapent. Et je sais m'arrêter — un projet à 95% en ligne vaut mieux qu'à 100% sur le disque.
            </p>
            <div className="hero-cta">
              <a className="btn btn-secondary" href="https://www.linkedin.com/in/fabrice-sousa-a49118393/" target="_blank" rel="noreferrer"><Icon.linkedin /> LinkedIn</a>
              <a className="btn btn-secondary" href="https://github.com/One-Up-Dev" target="_blank" rel="noreferrer"><Icon.github /> GitHub</a>
              <a className="btn btn-ghost" href="#">Télécharger CV ↓</a>
            </div>
          </div>
          {showSphere && <TagSphere speed={sphereSpeed} />}
        </div>
      </div>
    </section>
  );
}

// ============ TAG SPHERE ============
const SPHERE_TAGS = [
  // core (7) — fondamentaux data
  { t: 'Python', kind: 'core' },
  { t: 'pandas', kind: 'core' },
  { t: 'NumPy', kind: 'core' },
  { t: 'scikit-learn', kind: 'core' },
  { t: 'SQL', kind: 'core' },
  { t: 'matplotlib', kind: 'core' },
  { t: 'Seaborn', kind: 'core' },
  // eng (7) — bases, env, API, automation
  { t: 'MySQL', kind: 'eng' },
  { t: 'PostgreSQL', kind: 'eng' },
  { t: 'DuckDB', kind: 'eng' },
  { t: 'pip', kind: 'eng' },
  { t: 'uv', kind: 'eng' },
  { t: 'REST API', kind: 'eng' },
  { t: 'n8n', kind: 'eng' },
  // mlops (4) — versionning, conteneurs, BI
  { t: 'Docker', kind: 'mlops' },
  { t: 'Git', kind: 'mlops' },
  { t: 'GitHub', kind: 'mlops' },
  { t: 'PowerBI', kind: 'mlops' },
  // ia (5) — outils IA (anciennement `cloud`, repurposé)
  { t: 'Claude Code', kind: 'ia' },
  { t: 'Mistral', kind: 'ia' },
  { t: 'Gemini', kind: 'ia' },
  { t: 'prompt engineering', kind: 'ia' },
  { t: 'vibe-coding', kind: 'ia' },
  // domain (5) — types de problèmes
  { t: 'Classification', kind: 'domain' },
  { t: 'NLP', kind: 'domain' },
  { t: 'Time series', kind: 'domain' },
  { t: 'Dataviz', kind: 'domain' },
  { t: 'Automatisation IA', kind: 'domain' },
  // meta (7) — dispo + langues
  { t: 'Remote OK', kind: 'meta' },
  { t: 'CDI', kind: 'meta' },
  { t: 'Freelance', kind: 'meta' },
  { t: 'FR natif', kind: 'meta' },
  { t: 'PT natif', kind: 'meta' },
  { t: 'EN courant', kind: 'meta' },
  { t: 'ES intermédiaire', kind: 'meta' },
];

function TagSphere({ speed = 0.25 }) {
  const sphereRef = useRef(null);
  const stateRef = useRef({
    rotX: -10,
    rotY: 0,
    velX: 0,
    velY: speed,
    targetVelX: 0,
    targetVelY: speed,
    dragging: false,
    lastX: 0,
    lastY: 0,
  });
  useEffect(() => {
    stateRef.current.targetVelY = speed;
  }, [speed]);

  const RADIUS = 150;
  const positions = (() => {
    const n = SPHERE_TAGS.length;
    const phi = Math.PI * (3 - Math.sqrt(5));
    return SPHERE_TAGS.map((tag, i) => {
      const y = 1 - (i / (n - 1)) * 2;
      const r = Math.sqrt(1 - y * y);
      const theta = phi * i;
      return {
        ...tag,
        x: Math.cos(theta) * r * RADIUS,
        y: y * RADIUS,
        z: Math.sin(theta) * r * RADIUS,
      };
    });
  })();

  useEffect(() => {
    let raf;
    const tick = () => {
      const stage = sphereRef.current;
      if (!stage) {
        raf = requestAnimationFrame(tick);
        return;
      }
      const tagEls = stage.querySelectorAll('.sphere-tag');
      const s = stateRef.current;
      s.velX += (s.targetVelX - s.velX) * 0.08;
      s.velY += (s.targetVelY - s.velY) * 0.08;
      if (!s.dragging) {
        s.rotX += s.velX;
        s.rotY += s.velY;
      }
      const cosY = Math.cos((s.rotY * Math.PI) / 180);
      const sinY = Math.sin((s.rotY * Math.PI) / 180);
      const cosX = Math.cos((s.rotX * Math.PI) / 180);
      const sinX = Math.sin((s.rotX * Math.PI) / 180);
      positions.forEach((p, i) => {
        const x1 = p.x * cosY - p.z * sinY;
        const z1 = p.x * sinY + p.z * cosY;
        const y2 = p.y * cosX - z1 * sinX;
        const z2 = p.y * sinX + z1 * cosX;
        const el = tagEls[i];
        if (!el) return;
        const depth = (z2 + RADIUS) / (2 * RADIUS);
        const scale = 0.55 + depth * 0.55;
        const opacity = 0.35 + depth * 0.65;
        el.style.transform = `translate3d(${x1}px, ${y2}px, ${z2}px) scale(${scale.toFixed(3)})`;
        el.style.opacity = opacity.toFixed(3);
        el.style.zIndex = Math.round(depth * 100);
      });
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);

  const onPointerMove = (e) => {
    const s = stateRef.current;
    const rect = sphereRef.current.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    const dx = (e.clientX - cx) / rect.width;
    const dy = (e.clientY - cy) / rect.height;
    s.targetVelY = dx * 1.5;
    s.targetVelX = -dy * 1.0;
  };
  const onPointerLeave = () => {
    const s = stateRef.current;
    s.targetVelY = speed;
    s.targetVelX = 0;
  };
  const onPointerDown = (e) => {
    const s = stateRef.current;
    s.dragging = true;
    s.lastX = e.clientX;
    s.lastY = e.clientY;
    sphereRef.current.setPointerCapture?.(e.pointerId);
  };
  const onPointerUp = (e) => {
    const s = stateRef.current;
    s.dragging = false;
    sphereRef.current.releasePointerCapture?.(e.pointerId);
  };
  const onDragMove = (e) => {
    const s = stateRef.current;
    if (!s.dragging) return;
    const dx = e.clientX - s.lastX;
    const dy = e.clientY - s.lastY;
    s.rotY += dx * 0.5;
    s.rotX -= dy * 0.5;
    s.lastX = e.clientX;
    s.lastY = e.clientY;
  };

  return (
    <aside
      className="about-sphere reveal"
      id="stack"
      style={{ '--reveal-delay': '160ms' }}>
      <div
        ref={sphereRef}
        className="sphere-stage"
        onPointerMove={(e) => { onPointerMove(e); onDragMove(e); }}
        onPointerLeave={onPointerLeave}
        onPointerDown={onPointerDown}
        onPointerUp={onPointerUp}
      >
        <div className="sphere-glow" />
        {positions.map((p, i) => (
          <span key={i} className={`sphere-tag sphere-tag-${p.kind}`}>{p.t}</span>
        ))}
      </div>
      <div className="sphere-hint">
        <span className="sphere-hint-dot" />
        Stack technique · glissez pour explorer
      </div>
    </aside>
  );
}

// ============ CTA + FOOTER ============
function CTA() {
  return (
    <section className="cta-band" id="contact">
      <div className="container reveal">
        <span className="eyebrow" style={{ justifyContent: 'center', marginBottom: 16 }}>Recrutement · contact direct</span>
        <h2>Une mission, un projet data ?<br/>Parlons-en.</h2>
        <p>Réponse sous 24h en semaine. Décrivez le contexte et les enjeux, je vous dis si je peux aider.</p>
        <div className="hero-cta">
          <a className="btn btn-primary btn-lg" href="mailto:profab.tulle@gmail.com"><Icon.mail /> profab.tulle@gmail.com</a>
          <a className="btn btn-secondary btn-lg" href="https://www.linkedin.com/in/fabrice-sousa-a49118393/" target="_blank" rel="noreferrer"><Icon.linkedin /> Message LinkedIn</a>
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="footer">
      <div className="container">
        <div className="footer-grid">
          <div className="footer-brand">
            <div className="brand">
              <span className="brand-logo"><img src={logoUrl} alt="logo"/></span>
              <span className="brand-name"><span>Fabrice</span><span className="role">Data Scientist · IA · ML engineer</span></span>
            </div>
            <p>Portfolio personnel · prototypé avec Claude Design, codé avec Claude Code, déployé sur Vercel.</p>
          </div>
          <div className="footer-links">
            <h4>Navigation</h4>
            <a href="#projects">Projets</a>
            <a href="#about">À propos</a>
            <a href="#contact">Contact</a>
          </div>
          <div className="footer-links">
            <h4>Présence</h4>
            <a href="https://github.com/One-Up-Dev" target="_blank" rel="noreferrer">GitHub</a>
            <a href="https://www.linkedin.com/in/fabrice-sousa-a49118393/" target="_blank" rel="noreferrer">LinkedIn</a>
            <a href="https://oneup-blog.vercel.app/" target="_blank" rel="noreferrer">Blog</a>
            <a href="#">Google Scholar</a>
          </div>
        </div>
        <div className="footer-bottom">
          <span>© 2026 — OneUp. Tous droits réservés.</span>
          <span>Build with Claude Code · no tracking</span>
        </div>
      </div>
    </footer>
  );
}

// ============ APP ============
function App() {
  const [theme, setTheme] = useState(() => {
    const saved = localStorage.getItem('theme');
    if (saved === 'light' || saved === 'dark') return saved;
    return document.documentElement.getAttribute('data-theme') || 'light';
  });

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('theme', theme);
  }, [theme]);

  const toggleTheme = () => setTheme((t) => (t === 'dark' ? 'light' : 'dark'));

  return (
    <>
      <Nav theme={theme} onToggleTheme={toggleTheme} />
      <main>
        <Hero />
        <TrustBar />
        <Projects />
        <About />
        <CTA />
      </main>
      <Footer />
    </>
  );
}

createRoot(document.getElementById('root')).render(<App />);
